/**
 * Phase 66 Plan 04 — useMachines (Plan 06 extends with optional `extra`).
 *
 * Circuit-scoped TanStack Query hook backing `MachinesPage`. Built on
 * the central `qk` factory (Plan 02 step 4b) so the multi-tenant
 * cache-key invariant is preserved (Pitfall 8 + threat T-66-04-07).
 *
 * MachineDetailPage uses a derived key shape `[...qk.machines(tenant), cpuId]`
 * so per-machine entries still gate on the tenant.
 *
 * Plan 06 Task 2 (C-7 CommandPalette flicker): the optional `extra`
 * argument is shallow-merged into the useQuery options. CommandPalette
 * passes `{ placeholderData: keepPreviousData }` so the palette continues
 * to render the previous tenant's results during the ~500ms refetch
 * window after a tenant switch invalidates caches. Backend re-validates
 * RBAC on every request so showing stale-but-still-tenant-scoped data
 * is not a security risk.
 */

import {
  useQuery,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { listMachines, getMachine } from "@/api/machines";
import { useCircuitId } from "@/hooks/useCircuitId";
import { qk } from "@/lib/queryKeys";
import {
  pollingIntervalForWs,
  useWsConnectionState,
} from "@/stores/wsConnectionStore";
import type { Machine } from "@/types/api";

type ExtraOpts<TData> = Partial<
  Omit<UseQueryOptions<TData, Error, TData>, "queryKey" | "queryFn">
>;

export function useMachines(
  extra?: ExtraOpts<Machine[]>,
  /**
   * 2026-06-05 hotfix — Phase 70 CloneFromTemplateModal needs to list
   * candidate target machines from the TEMPLATE's circuit, not from the
   * active tenant's circuit (the active tenant may differ). When
   * `circuitOverride` is supplied, bypass `useCircuitId()` and scope
   * the query to the given circuit. Cache key uses the override so the
   * main MachinesPage cache is not polluted across tenant switches.
   * Pass `null` explicitly to force a fleet-wide list (super_admin only —
   * backend rejects when role lacks CROSS_CIRCUIT_ROLES).
   */
  circuitOverride?: string | null | undefined,
) {
  const activeCircuitId = useCircuitId();
  const circuitId =
    circuitOverride === undefined ? activeCircuitId : circuitOverride;
  const tenant = circuitId ?? "all";
  // Phase 67-05 Wave 6 — when the WS is healthy, drop polling to a 5min
  // safety net (WS pushes are the primary update path). When the WS is
  // down or in fallback, keep the legacy 30s cadence so the operator
  // never sees stuck data.
  const ws = useWsConnectionState();
  const refetchInterval = pollingIntervalForWs(ws);
  return useQuery({
    queryKey: qk.machines(tenant),
    queryFn: () => listMachines(circuitId),
    staleTime: 10_000,
    refetchInterval,
    ...extra,
  });
}

export function useMachineDetail(cpuId: string | undefined) {
  const circuitId = useCircuitId();
  const tenant = circuitId ?? "all";
  return useQuery({
    // Derived key — keeps the tenant scope at the front so Plan 06
    // tenant switching invalidates per-machine entries automatically.
    queryKey: [...qk.machines(tenant), cpuId] as const,
    queryFn: () => getMachine(cpuId as string),
    enabled: !!cpuId,
    staleTime: 5_000,
  });
}
